import { oc } from "@orpc/contract";
import { WAREHOUSE_HIERARCHY_RELATIONSHIP_TYPE_VALUES } from "@erp/shared-types";
import { z } from "zod";

// ─── Schemas ────────────────────────────────────────────────────────────────

export const WarehouseHierarchyRelationshipTypeSchema = z.enum(
  WAREHOUSE_HIERARCHY_RELATIONSHIP_TYPE_VALUES,
);

export const WarehouseHierarchyCounterpartSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  type: z.string().nullable(),
  divisionId: z.string().uuid().nullable(),
  divisionName: z.string().nullable(),
});

export const WarehouseHierarchyEdgeSchema = z.object({
  id: z.string().uuid(),
  parentWarehouseId: z.string().uuid(),
  childWarehouseId: z.string().uuid(),
  relationshipType: WarehouseHierarchyRelationshipTypeSchema,
  sourcingPriority: z.number().int(),
  crossDivisionAllowed: z.boolean(),
  activeFrom: z.string(),
  activeTo: z.string().nullable(),
  /**
   * The "other side" of the edge from the perspective of the warehouse the
   * caller asked about — the parent when listing upstream, the child when
   * listing downstream.
   */
  counterpart: WarehouseHierarchyCounterpartSchema,
});

// ─── List ───────────────────────────────────────────────────────────────────

export const ListWarehouseHierarchyInputSchema = z.object({
  warehouseId: z.string().uuid(),
  /** Include rows where activeTo is in the past. Default false. */
  includeInactive: z.coerce.boolean().optional().default(false),
});

export const ListWarehouseHierarchyResponseSchema = z.object({
  /** Edges where the requested warehouse is the child (sources upstream). */
  upstream: z.array(WarehouseHierarchyEdgeSchema),
  /** Edges where the requested warehouse is the parent (supplies downstream). */
  downstream: z.array(WarehouseHierarchyEdgeSchema),
});

// ─── Network (whole-org graph) ──────────────────────────────────────────────

export const NetworkWarehouseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  type: z.string().nullable(),
  divisionId: z.string().uuid().nullable(),
  divisionName: z.string().nullable(),
});

export const NetworkEdgeSchema = z.object({
  id: z.string().uuid(),
  parentWarehouseId: z.string().uuid(),
  childWarehouseId: z.string().uuid(),
  relationshipType: WarehouseHierarchyRelationshipTypeSchema,
  sourcingPriority: z.number().int(),
  crossDivisionAllowed: z.boolean(),
});

export const ListNetworkInputSchema = z.object({
  /** When true, return *all* warehouses regardless of edge presence. Default true. */
  includeOrphans: z.coerce.boolean().optional().default(true),
});

export const ListNetworkResponseSchema = z.object({
  warehouses: z.array(NetworkWarehouseSchema),
  edges: z.array(NetworkEdgeSchema),
});

// ─── Create / update / soft-close / delete ──────────────────────────────────

const createBaseShape = {
  parentWarehouseId: z.string().uuid(),
  childWarehouseId: z.string().uuid(),
  relationshipType: WarehouseHierarchyRelationshipTypeSchema,
  sourcingPriority: z.number().int().min(0).max(1_000_000),
  crossDivisionAllowed: z.boolean().optional().default(false),
  activeFrom: z.string().optional(),
  activeTo: z.string().nullable().optional(),
};

export const CreateWarehouseHierarchyInputSchema = z
  .object(createBaseShape)
  .refine((input) => input.parentWarehouseId !== input.childWarehouseId, {
    message: "A warehouse cannot be its own parent or child.",
    path: ["childWarehouseId"],
  });

export const UpdateWarehouseHierarchyInputSchema = z.object({
  id: z.string().uuid(),
  relationshipType: WarehouseHierarchyRelationshipTypeSchema.optional(),
  sourcingPriority: z.number().int().min(0).max(1_000_000).optional(),
  crossDivisionAllowed: z.boolean().optional(),
  activeFrom: z.string().optional(),
  activeTo: z.string().nullable().optional(),
});

export const SoftCloseWarehouseHierarchyInputSchema = z.object({
  id: z.string().uuid(),
  /** Date to set as activeTo. Defaults to today on the server when omitted. */
  activeTo: z.string().optional(),
});

export const DeleteWarehouseHierarchyInputSchema = z.object({
  id: z.string().uuid(),
});

export const WarehouseHierarchyMutationResponseSchema = z.object({
  success: z.boolean(),
});

// ─── Contract ───────────────────────────────────────────────────────────────

export const warehouseHierarchyContracts = {
  listForWarehouse: oc
    .input(ListWarehouseHierarchyInputSchema)
    .output(ListWarehouseHierarchyResponseSchema)
    .route({
      method: "GET",
      summary:
        "List upstream and downstream hierarchy edges for a single warehouse.",
    }),
  listNetwork: oc
    .input(ListNetworkInputSchema)
    .output(ListNetworkResponseSchema)
    .route({
      method: "GET",
      summary:
        "Return all warehouses + all active hierarchy edges for the org. Frontend assembles the tree.",
    }),
  create: oc
    .input(CreateWarehouseHierarchyInputSchema)
    .output(WarehouseHierarchyEdgeSchema)
    .route({
      summary: "Create a sourcing relationship between two warehouses",
    }),
  update: oc
    .input(UpdateWarehouseHierarchyInputSchema)
    .output(WarehouseHierarchyEdgeSchema)
    .route({ summary: "Update a hierarchy edge in place" }),
  softClose: oc
    .input(SoftCloseWarehouseHierarchyInputSchema)
    .output(WarehouseHierarchyMutationResponseSchema)
    .route({
      summary:
        "Mark a hierarchy edge inactive by setting activeTo (preserves history).",
    }),
  delete: oc
    .input(DeleteWarehouseHierarchyInputSchema)
    .output(WarehouseHierarchyMutationResponseSchema)
    .route({
      summary:
        "Hard-delete a hierarchy edge. Use only for typo correction; prefer softClose.",
    }),
} as const;

// ─── Types ──────────────────────────────────────────────────────────────────

export type WarehouseHierarchyEdge = z.infer<
  typeof WarehouseHierarchyEdgeSchema
>;
export type WarehouseHierarchyCounterpart = z.infer<
  typeof WarehouseHierarchyCounterpartSchema
>;
export type ListWarehouseHierarchyInput = z.infer<
  typeof ListWarehouseHierarchyInputSchema
>;
export type ListWarehouseHierarchyResponse = z.infer<
  typeof ListWarehouseHierarchyResponseSchema
>;
export type CreateWarehouseHierarchyInput = z.infer<
  typeof CreateWarehouseHierarchyInputSchema
>;
export type UpdateWarehouseHierarchyInput = z.infer<
  typeof UpdateWarehouseHierarchyInputSchema
>;
export type SoftCloseWarehouseHierarchyInput = z.infer<
  typeof SoftCloseWarehouseHierarchyInputSchema
>;
export type DeleteWarehouseHierarchyInput = z.infer<
  typeof DeleteWarehouseHierarchyInputSchema
>;
export type NetworkWarehouse = z.infer<typeof NetworkWarehouseSchema>;
export type NetworkEdge = z.infer<typeof NetworkEdgeSchema>;
export type ListNetworkInput = z.infer<typeof ListNetworkInputSchema>;
export type ListNetworkResponse = z.infer<typeof ListNetworkResponseSchema>;
